/**
 * WEB-925 атом 5 — режимы подачи цены (`StylePack.paywall.priceMode`).
 *
 * ⚠ ЭТО ДЕНЬГИ НА ЭКРАНЕ. Модуль ТОЛЬКО ФОРМАТИРУЕТ: priceId, сумма чекаута,
 * Stripe и вся логика оплаты не трогаются ни одной строкой. Пересчёт — приём
 * подачи («дешевле чашки кофе»), а не другая цена.
 *
 * Правила, которые здесь зашиты (SYNTHESIS.md §6 п.5):
 *
 *   1. Период берётся ИЗ ДАННЫХ (`interval` + `intervalCount`, приезжают из
 *      Stripe через PublicStripePrice). Из текста период не выводится никогда.
 *   2. «За месяц» считается только ВНИЗ — из MONTH/YEAR. Неделя и день вверх до
 *      месяца не домножаются: показать сумму, которую одним платежом никогда не
 *      спишут, — обман.
 *   3. Округление — до целой минорной единицы, half-up (Math.round).
 *   4. Если пересчёт округляется в 0 — пересчёта НЕ показываем совсем.
 *      «$0.00 в день» читается покупателем как «бесплатно».
 *   5. При любом пересчёте рядом остаётся НАСТОЯЩЕЕ списание с периодом
 *      (`actual`) — строкой, идентичной сегодняшней (`formatStripePrice`).
 *   6. Валюта не конвертируется и не подменяется — только та, что в цене.
 *   7. Любой мусор на входе → тихий откат к сегодняшнему виду.
 */
import { formatPriceAmount, formatStripePrice, type PublicStripePrice } from './formatStripePrice';
// WEB-1821: слова («в день», «Потом …», «N дней бесплатно») — из словаря по
// языку воронки; арифметика и валюта остаются здесь и от языка не зависят.
import {
  getPublicQuizStrings,
  type PublicQuizStrings,
} from './publicQuizStrings';

/** Совпадает с `StylePack['paywall']['priceMode']`. */
export type PriceDisplayMode = 'per-month' | 'per-day' | 'trial-total';

export interface PriceDisplay {
  /** Главная строка — то, что показываем крупно. */
  main: string;
  /** Подпись под ней: единица («в день») или период («за 2 недели»). '' — подписи нет. */
  unitLabel: string;
  /** «Списание: …» / «Потом …» — вторая строка. null — второй строки нет. */
  actual: string | null;
  /** true — `main` получен делением; false — это сырая сумма из Stripe. */
  recalculated: boolean;
  /**
   * WEB-1014 п.3: `actual` — не подпись, а полноценная вторая строка, рендерер
   * обязан дать ей заметный кегль. Взводится в режиме акцента на пробном
   * периоде: крупное «7 дней бесплатно» с мелким «потом $19.99» под ним — это
   * и есть тот приём, из-за которого покупатель не замечает списания.
   */
  actualProminent: boolean;
}

/**
 * Средняя длина месяца. 365/12 = 30.41(6): годовая и месячная цены при таком
 * делителе дают согласованный «в день» (годовая/365 == месячная*12/365).
 */
const DAYS_PER_MONTH = 365 / 12;
const DAYS_PER_YEAR = 365;

/**
 * Сумма пригодна к делению: целое положительное число минорных единиц.
 *
 * ЭКСПОРТИРУЕТСЯ НАМЕРЕННО (WEB-1822, доводка 31.08): правило 7 нужно не
 * только пересчёту «в день», но и веткам, которые печатают ГЛАВНУЮ сумму —
 * иначе непригодная цена доезжает до покупателя как «$0.00». Второй копии
 * этой проверки в рендерерах быть не должно (шрам «разошедшаяся копия
 * правила»).
 */
export function isUsableAmount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

/** `intervalCount` из Stripe: целое >= 1. Отсутствует — считается за 1. */
function periodCount(price: PublicStripePrice): number | null {
  if (price.intervalCount === undefined || price.intervalCount === null) return 1;
  const n = price.intervalCount;
  if (!Number.isSafeInteger(n) || n < 1) return null;
  return n;
}

/** Длина одного цикла списания в днях. null — цикла нет или он неизвестен. */
function cycleDays(price: PublicStripePrice): number | null {
  const n = periodCount(price);
  if (n === null) return null;
  switch (price.interval) {
    case 'DAY':
      return n;
    case 'WEEK':
      return n * 7;
    case 'MONTH':
      return n * DAYS_PER_MONTH;
    case 'YEAR':
      return n * DAYS_PER_YEAR;
    default:
      return null;
  }
}

/**
 * Длина цикла в месяцах — только там, где она выражается месяцами точно.
 * Для WEEK/DAY возвращается null намеренно: см. правило 2 в шапке файла.
 */
function cycleMonths(price: PublicStripePrice): number | null {
  const n = periodCount(price);
  if (n === null) return null;
  switch (price.interval) {
    case 'MONTH':
      return n;
    case 'YEAR':
      return n * 12;
    default:
      return null;
  }
}

/** Период словами: «за 2 недели» / «for 2 weeks». '' — периода нет. */
function periodPhrase(
  price: PublicStripePrice,
  strings: PublicQuizStrings,
): string {
  const n = periodCount(price);
  if (n === null) return '';
  return strings.pricePeriodPhrase(price.interval, n);
}

/**
 * WEB-1826: период тарифа словом — «в месяц» / «a month» / «каждые 2 недели».
 *
 * Живёт здесь, а не в рендерере, по той же причине, что и `isUsableAmount`:
 * смысл `interval`/`intervalCount` знает ОДИН файл (periodCount выше —
 * единственное место, где битый intervalCount превращается в «периода нет»).
 * Копия этой логики в ветке блока разошлась бы с этой при первой же правке.
 *
 * '' означает «периода нет»: разовый платёж (ONE_TIME мимо словаря), чужой
 * интервал или битый intervalCount. Подставлять покупателю нечего.
 */
export function pricePeriodWord(
  price: PublicStripePrice,
  strings: PublicQuizStrings,
): string {
  const n = periodCount(price);
  if (n === null) return '';
  return strings.pricePerPeriodPhrase(price.interval, n);
}

/**
 * WEB-1826: плейсхолдер периода в авторском тексте — «Всего _ {{period}}».
 * Приём тот же, что у шаблона условий payment_terms с его {{price}}.
 *
 * ДВА свойства, ради которых это отдельная функция с тестами:
 *
 *  1) ТЕКСТ БЕЗ ПЛЕЙСХОЛДЕРА ВОЗВРАЩАЕТСЯ ТЕМ ЖЕ ЗНАЧЕНИЕМ, побайтно —
 *     двойные пробелы, хвосты, чужие {{price}}. Живые воронки писали слово
 *     периода руками; «улучшить» их задним числом мы не имеем права, и
 *     безусловная нормализация пробелов сделала бы ровно это.
 *  2) ПУСТОЕ СЛОВО НЕ ОСТАВЛЯЕТ ДЫРЫ. Пробел перед плейсхолдером съедается
 *     вместе с ним, поэтому «Всего {{period}}, платёж один» на разовом
 *     платеже даёт «Всего, платёж один», а не «Всего , платёж один».
 */
const PERIOD_PLACEHOLDER = /[ \t]*\{\{\s*period\s*\}\}/g;

export function substitutePeriodPlaceholder(
  text: string,
  periodWord: string,
): string;
export function substitutePeriodPlaceholder(
  text: string | undefined,
  periodWord: string,
): string | undefined;
export function substitutePeriodPlaceholder(
  text: string | undefined,
  periodWord: string,
): string | undefined {
  if (!text) return text;
  const substituted = text.replace(
    PERIOD_PLACEHOLDER,
    periodWord ? ` ${periodWord}` : '',
  );
  // Плейсхолдера не было — отдаём исходную строку, а не её копию.
  if (substituted === text) return text;
  return substituted.trim();
}

/** Сегодняшний вид: строка как была, без пересчёта и без подписи. */
function rawDisplay(price: PublicStripePrice): PriceDisplay {
  return {
    main: formatStripePrice(price),
    unitLabel: '',
    actual: null,
    recalculated: false,
    actualProminent: false,
  };
}

/**
 * WEB-1014 п.3: длительность пробного периода в днях, если она есть В ДАННЫХ.
 *
 * Условий два, и оба обязательны:
 *   • `trialPeriodDays` — целое положительное (приезжает из Stripe);
 *   • у цены есть цикл списания (`cycleDays`). Разовый платёж с пробным
 *     периодом — противоречие: списание одно и оно происходит сразу, обещать
 *     «бесплатно» перед ним нельзя.
 * Не выполнено — акцентировать нечего, вызывающий откатывается к прежней подаче.
 */
function trialDays(price: PublicStripePrice): number | null {
  const days = price.trialPeriodDays;
  if (typeof days !== 'number' || !Number.isSafeInteger(days) || days < 1) return null;
  if (cycleDays(price) === null) return null;
  return days;
}

/**
 * Правила 3 и 4 в одном месте: деление half-up до целой минорной единицы;
 * null — делить не на что (divisor <= 1) или округлилось в ноль.
 *
 * Вынесено из `divided`, чтобы у пересчёта была РОВНО ОДНА арифметика:
 * ею пользуется и подача цены пака (ниже), и блок «Цена по частям»
 * (WEB-1822) через `perDayMinorUnits`. Вторая копия деления рано или поздно
 * разошлась бы с этой — и один и тот же тариф показал бы покупателю две
 * разные цены «в день» в соседних блоках одного экрана.
 */
function dividedMinorUnits(amount: number, divisor: number | null): number | null {
  if (divisor === null || !Number.isFinite(divisor) || divisor <= 1) return null;
  const derived = Math.round(amount / divisor);
  return derived < 1 ? null : derived;
}

/**
 * WEB-1822: сумма ЗА ДЕНЬ в минорных единицах — то же деление, что делает
 * режим подачи 'per-day', но без форматирования и без второй строки.
 *
 * null означает «предложения про день не будет»:
 *   • сумма непригодна (правило 7) или цикла списания нет вовсе (разовый
 *     платёж — делить не на что, придумывать «в день» для него нельзя);
 *   • пересчёт округлился в ноль (правило 4: «$0.00 в день» читается как
 *     «бесплатно»).
 * Цикл ровно в сутки — цена УЖЕ дневная, возвращается как есть.
 */
export function perDayMinorUnits(price: PublicStripePrice): number | null {
  if (!isUsableAmount(price.unitAmount)) return null;
  const days = cycleDays(price);
  if (days === null || !Number.isFinite(days) || days < 1) return null;
  if (days === 1) return price.unitAmount;
  return dividedMinorUnits(price.unitAmount, days);
}

function divided(
  price: PublicStripePrice,
  divisor: number | null,
  unitLabel: string,
  strings: PublicQuizStrings,
): PriceDisplay {
  // Делить не на что (divisor <= 1) — сырая цена уже и есть цена за единицу;
  // округлилось в ноль (правило 4) — пересчёт не показываем вовсе.
  const derived = dividedMinorUnits(price.unitAmount, divisor);
  if (derived === null) return rawDisplay(price);

  return {
    main: formatPriceAmount(derived, price.currency),
    unitLabel,
    // Правило 5: настоящее списание остаётся на экране рядом.
    actual: strings.priceBilled(formatStripePrice(price)),
    recalculated: true,
    actualProminent: false,
  };
}

/**
 * Как показать цену тарифа при заданном режиме пака.
 * `mode` не задан (воронка без пака) → ровно сегодняшний вид.
 */
export function derivePriceDisplay(
  price: PublicStripePrice,
  mode?: PriceDisplayMode,
  // WEB-1821: язык воронки; нет/неизвестен → en (конвенция словаря WEB-785).
  language?: string | null,
): PriceDisplay {
  const strings = getPublicQuizStrings(language);
  if (!mode) return rawDisplay(price);
  // Правило 7: без пригодной суммы ничего не считаем.
  if (!isUsableAmount(price.unitAmount)) return rawDisplay(price);

  switch (mode) {
    case 'per-month':
      return divided(price, cycleMonths(price), strings.priceUnitPerMonth, strings);
    case 'per-day':
      return divided(price, cycleDays(price), strings.priceUnitPerDay, strings);
    case 'trial-total': {
      // WEB-1014 п.3: у цены с пробным периодом на первом плане ЕГО ДЛИТЕЛЬНОСТЬ,
      // а цена после него — обязательная вторая строка. Сумма не трогается:
      // в `actual` уходит ровно сегодняшняя строка с периодом.
      const days = trialDays(price);
      if (days !== null) {
        return {
          main: strings.priceTrialFree(days),
          unitLabel: '',
          actual: strings.priceThen(formatStripePrice(price)),
          recalculated: false,
          actualProminent: true,
        };
      }
      // Пробного периода в цене нет — придумывать его нельзя. Сумма НЕ трогается,
      // период просто уезжает из строки в подпись (подача «за период»).
      const phrase = periodPhrase(price, strings);
      if (!phrase) return rawDisplay(price);
      return {
        main: formatPriceAmount(price.unitAmount, price.currency),
        unitLabel: phrase,
        actual: null,
        recalculated: false,
        actualProminent: false,
      };
    }
    default:
      return rawDisplay(price);
  }
}
